<template>
  <main class="sf-page">
    <div v-if="loading" class="sf-loader"><span class="sf-loader__ring"></span></div>
    <div v-else-if="error" class="sf-section sf-section--cream">
      <div class="sf-container sf-error">
        <div class="sf-error__icon-wrap"><X /></div>
        <h2 class="sf-h2">Association non trouvée</h2>
        <p style="color:var(--ink-60);margin:.5rem 0 1.5rem">{{ error }}</p>
        <router-link to="/associations"><button class="sf-btn sf-btn--terra"><ArrowLeft class="sf-btn__icon" />Retour</button></router-link>
      </div>
    </div>

    <template v-else-if="association">
      <!-- Cover hero -->
      <div class="sa-cover">
        <img v-if="association.cover_image" :src="imageUrl+association.cover_image" :alt="association.name" loading="lazy" class="sa-cover__img" />
        <div v-else class="sa-cover__fallback"><Users /></div>
        <div class="sa-cover__overlay"></div>
        <div class="sa-cover__logo">
          <img v-if="association.logo" :src="imageUrl+association.logo" :alt="association.name" loading="lazy" />
          <Users v-else />
        </div>
        <div class="sa-cover__badges">
          <span v-if="association.is_featured" class="sf-card__badge" style="position:static">À la une</span>
          <span class="sf-card__cat" style="position:static">{{ association.category }}</span>
        </div>
      </div>

      <section class="sf-section sf-section--cream">
        <div class="sf-container">
          <router-link to="/associations" class="sf-back-link"><ArrowLeft />Retour aux associations</router-link>

          <!-- Title -->
          <div style="margin-top:3.5rem;margin-bottom:2.5rem">
            <h1 class="sf-h2" style="font-size:clamp(2rem,5vw,3.5rem);margin-bottom:.75rem">{{ association.name }}</h1>
            <p style="font-size:1rem;color:var(--ink-60);line-height:1.7;max-width:60ch; word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;">{{ association.description }}</p>
          </div>

          <!-- Key info -->
          <div class="sa-info-grid">
            <div class="sa-info-chip"><Calendar /><div><small>Partenaire depuis</small><strong>{{ association.formatted_partnership_date||association.partnership_since }}</strong></div></div>
            <div class="sa-info-chip"><MapPin /><div><small>Localisation</small><strong>{{ association.location }}</strong></div></div>
            <div class="sa-info-chip"><Target /><div><small>Projets actifs</small><strong>{{ association.active_projects||0 }}</strong></div></div>
            <div class="sa-info-chip"><Users /><div><small>Membres</small><strong>{{ association.members_count||'N/A' }}</strong></div></div>
          </div>

          <!-- Content + Sidebar -->
          <div class="sa-layout">
            <div class="sa-main">
              <div class="sf-sidebar-card" style="margin-bottom:1.5rem">
                <div class="sf-sidebar-card__header"><h3>À propos</h3></div>
                <div class="sf-sidebar-card__body"><p style="font-size:.9rem;color:var(--ink-60);line-height:1.7">{{ association.full_description||association.description }}</p></div>
              </div>
              <div v-if="association.objectives?.length" class="sf-sidebar-card" style="margin-bottom:1.5rem">
                <div class="sf-sidebar-card__header"><h3>Objectifs</h3></div>
                <div class="sf-sidebar-card__body">
                  <ul class="sa-list">
                    <li v-for="(obj,i) in association.objectives" :key="i"><CheckCircle />{{ obj }}</li>
                  </ul>
                </div>
              </div>
              <div v-if="association.achievements?.length" class="sf-sidebar-card">
                <div class="sf-sidebar-card__header"><h3>Réalisations</h3></div>
                <div class="sf-sidebar-card__body">
                  <ul class="sa-list">
                    <li v-for="(ach,i) in association.achievements" :key="i"><Award />{{ ach }}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="sa-sidebar">
              <div class="sf-sidebar-card">
                <div class="sf-sidebar-card__header"><h3>Contact</h3></div>
                <div class="sf-sidebar-card__body sa-contact">
                  <a v-if="association.email" :href="`mailto:${association.email}`" class="sf-chip sa-contact-row"><Mail />{{ association.email }}</a>
                  <a v-if="association.phone" :href="`tel:${association.phone}`" class="sf-chip sa-contact-row"><Phone />{{ association.phone }}</a>
                  <a v-if="association.website" :href="association.website" target="_blank" class="sf-chip sa-contact-row"><Globe />Site web</a>
                  <span v-if="association.address" class="sf-chip sa-contact-row"><MapPin />{{ association.address }}</span>
                  <div v-if="association.social_links" class="sf-socials" style="margin-top:1rem">
                    <a v-if="association.social_links.facebook" :href="association.social_links.facebook" target="_blank" class="sf-social-btn"><Facebook /></a>
                    <a v-if="association.social_links.twitter" :href="association.social_links.twitter" target="_blank" class="sf-social-btn"><Twitter /></a>
                    <a v-if="association.social_links.linkedin" :href="association.social_links.linkedin" target="_blank" class="sf-social-btn"><Linkedin /></a>
                  </div>
                </div>
              </div>
              <div v-if="association.team?.length" class="sf-sidebar-card" style="margin-top:1.25rem">
                <div class="sf-sidebar-card__header"><h3>Équipe</h3></div>
                <div class="sf-sidebar-card__body">
                  <div v-for="(m,i) in association.team.slice(0,3)" :key="i" class="sa-team-row">
                    <div class="sf-avatar sa-team-avatar">{{ getInitials(m.name) }}</div>
                    <div><p style="font-size:.9rem;font-weight:500;color:var(--ink)">{{ m.name }}</p><p style="font-size:.76rem;color:var(--ink-60)">{{ m.role }}</p></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Gallery -->
          <div v-if="association.gallery?.length" style="margin-top:3rem">
            <span class="sf-label">Galerie</span>
            <div class="sf-gallery" style="margin-top:.75rem">
              <div v-for="(img,i) in association.gallery.slice(0,4)" :key="i" class="sf-gallery-item" style="height:180px" @click="openGallery(i)">
                <img :src="imageUrl+img" loading="lazy" :alt="`Galerie ${i+1}`" />
                <div class="sf-gallery-item__overlay"><ZoomIn /></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Gallery Modal -->
      <div v-if="showGalleryModal" class="sf-modal" @click.self="closeGallery">
        <button class="sf-modal__close" @click="closeGallery"><X /></button>
        <div style="position:relative;max-width:900px;width:100%">
          <button v-if="currentGalleryIndex>0" class="sf-modal__nav sf-modal__nav--prev" @click="prevImage"><ChevronLeft /></button>
          <img :src="association.gallery[currentGalleryIndex].url||imageUrl+association.gallery[currentGalleryIndex]" class="sa-modal-img" />
          <button v-if="currentGalleryIndex<association.gallery.length-1" class="sf-modal__nav sf-modal__nav--next" @click="nextImage"><ChevronRight /></button>
          <p class="sf-modal__counter">{{ currentGalleryIndex+1 }} / {{ association.gallery.length }}</p>
        </div>
      </div>
    </template>
    <Transition name="toast"><div v-if="toastMessage" class="sf-toast"><CheckCircle />{{ toastMessage }}</div></Transition>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import AOS from 'aos'; import 'aos/dist/aos.css'
import { ArrowLeft, ArrowRight, Users, MapPin, Calendar, Target, Award, Mail, Phone, Globe, Facebook, Twitter, Linkedin, Instagram, CheckCircle, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-vue-next'
export default {
  name:'AssociationDetail',
  components:{ ArrowLeft, ArrowRight, Users, MapPin, Calendar, Target, Award, Mail, Phone, Globe, Facebook, Twitter, Linkedin, Instagram, CheckCircle, X, ChevronLeft, ChevronRight, ZoomIn },
  data(){return{showGalleryModal:false,currentGalleryIndex:0,toastMessage:''}},
  computed:{...mapState({association:s=>s.currentAssociation,similarAssociations:s=>s.similarAssociations,loading:s=>s.loading,error:s=>s.error,imageUrl:s=>s.imageUrl})},
  watch:{'$route.params.slug':{handler(s){if(s)this.loadAssociation(s)},immediate:true},toastMessage(v){if(v)setTimeout(()=>{this.toastMessage=''},3000)}},
  mounted(){AOS.init({once:true,offset:70,easing:'ease-out-cubic',duration:650})},
  methods:{
    ...mapActions(['fetchAssociationBySlug']),
    async loadAssociation(s){try{await this.fetchAssociationBySlug(s)}catch(e){console.error(e)}},
    getInitials(n){return n?n.split(' ').map(x=>x[0]).join('').toUpperCase().slice(0,2):'?'},
    openGallery(i){this.currentGalleryIndex=i;this.showGalleryModal=true;document.body.style.overflow='hidden'},
    closeGallery(){this.showGalleryModal=false;document.body.style.overflow='auto'},
    prevImage(){if(this.currentGalleryIndex>0)this.currentGalleryIndex--},
    nextImage(){if(this.currentGalleryIndex<this.association.gallery.length-1)this.currentGalleryIndex++}
  },
  beforeUnmount(){document.body.style.overflow='auto'}
}
</script>
<style>@import './sf-design.css';</style>
<style scoped>
.sa-cover{position:relative;height:360px;overflow:hidden;background:var(--forest)}
.sa-cover__img{width:100%;height:100%;object-fit:cover}
.sa-cover__fallback{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:var(--sage)}
.sa-cover__fallback :deep(svg){width:80px;height:80px}
.sa-cover__overlay{position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,.5),transparent)}
.sa-cover__logo{position:absolute;bottom:-36px;left:2rem;width:80px;height:80px;border-radius:12px;background:var(--white);border:4px solid var(--white);box-shadow:0 8px 24px rgba(0,0,0,.15);display:flex;align-items:center;justify-content:center;overflow:hidden}
.sa-cover__logo img{width:58px;height:58px;object-fit:contain}
.sa-cover__logo :deep(svg){width:34px;height:34px;color:var(--terra)}
.sa-cover__badges{position:absolute;top:1.25rem;right:1.25rem;display:flex;gap:.5rem}
.sa-info-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:1rem;margin-bottom:3rem}
.sa-info-chip{background:var(--white);border-radius:8px;padding:1rem;display:flex;align-items:flex-start;gap:.75rem;box-shadow:var(--shadow-sm)}
.sa-info-chip :deep(svg){width:18px;height:18px;color:var(--terra);flex-shrink:0;margin-top:2px}
.sa-info-chip small{display:block;font-size:.72rem;color:var(--ink-60);margin-bottom:.2rem}
.sa-info-chip strong{font-size:.92rem;color:var(--ink);font-weight:500}
.sa-layout{display:grid;grid-template-columns:1fr 320px;gap:2rem;align-items:start}
@media(max-width:900px){.sa-layout{grid-template-columns:1fr}}
.sa-list{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:.75rem}
.sa-list li{display:flex;align-items:flex-start;gap:.6rem;font-size:.88rem;color:var(--ink-60);line-height:1.55}
.sa-list li :deep(svg){width:16px;height:16px;color:var(--terra);flex-shrink:0;margin-top:2px}
.sa-contact{display:flex;flex-direction:column;gap:.6rem}
.sa-contact-row{text-decoration:none;font-size:.85rem;color:var(--ink-60);transition:color .2s ease}
.sa-contact-row:hover{color:var(--terra)}
.sa-team-row{display:flex;align-items:center;gap:.75rem;padding:.5rem 0;border-bottom:1px solid var(--ink-20)}
.sa-team-row:last-child{border-bottom:none}
.sa-team-avatar{width:38px;height:38px;font-size:.85rem}
.sa-modal-img{max-height:80vh;margin:0 auto;display:block;object-fit:contain}
</style>
